// ./src/components/features/ScheduleEventsTable/ScheduleEventsTable.tsx
import React, { useMemo } from "react";
import { Badge, Text, Group, Stack } from "@mantine/core";

import { formatDate, formatUTCTimeRange, ScheduleEvent } from "@/utils";

import {
  AppTable,
  SortConfig,
  AppTableColumn,
  EventStatusBadge,
  RecordActions,
} from "@/components/generic";

import { ScheduleEventTypeBadge } from "../ScheduleEventTypeBadge";
import { EventRoomsInfoBadge } from "../EventRoomsInfoBadge";
import { EventTeamsInfoBadge } from "../EventTeamsInfoBadge";
import { TagsBadge } from "../TagsBadge";

export interface ScheduleEventsTableProps {
  /** Array of events to display */
  events: ScheduleEvent[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditEvent?: (event: ScheduleEvent) => void;

  /** Callback when remove action is clicked */
  onRemoveEvent?: (event: ScheduleEvent) => void;

  /** Callback when view action is clicked */
  onViewEvent?: (event: ScheduleEvent) => void;

  /** Callback when view recurring event is clicked */
  onViewRecurringEvent?: (event: ScheduleEvent) => void;

  /** ID of event currently being removed */
  removingEventId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  disabled?: boolean;

  hideOnNoRecords?: boolean;
}

export const ScheduleEventsTable: React.FC<ScheduleEventsTableProps> = ({
  events,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  disabled = false,
  onSortChange,
  onLoadMore,
  onEditEvent,
  onRemoveEvent,
  onViewEvent,
  onViewRecurringEvent,
  removingEventId,
  hideOnNoRecords,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<ScheduleEvent>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Event Name",
        sortable: true,
        flex: true,
        minWidth: 200,
        render: (value, record: ScheduleEvent) => {
          const opacity = record.isPast ? 0.6 : 1;
          return (
            <Stack gap={0} style={{ opacity }}>
              <Group gap="xs" align="center">
                <Text fw={500}>{value || "Untitled Event"}</Text>
                {record.isPast && (
                  <Badge color="gray" variant="outline" size="xs">
                    Past
                  </Badge>
                )}
              </Group>
              <EventTeamsInfoBadge event={record} />
              <ScheduleEventTypeBadge event={record} />
              <TagsBadge
                value={record.categories?.map(
                  (category) => category?.name ?? ""
                )}
              />
            </Stack>
          );
        },
      },
      {
        key: "eventDate",
        label: "Date",
        sortable: true,
        width: 160,
        minWidth: 160,
        render: (value, record) => {
          const opacity = record.isPast ? 0.6 : 1;
          return (
            <Text fw={500} style={{ opacity }}>
              {formatDate(value)}
            </Text>
          );
        },
      },
      {
        key: "startTime",
        label: "Time",
        width: 180,
        minWidth: 180,
        render: (value, record) => {
          const opacity = record.isPast ? 0.6 : 1;
          return (
            <Text style={{ opacity }}>
              {formatUTCTimeRange(value, record.endTime, "h:mm A", true)}
            </Text>
          );
        },
      },
      {
        key: "room",
        label: "Room",
        width: 250,
        minWidth: 250,
        render: (_, record) => <EventRoomsInfoBadge event={record} />,
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        render: (value) => {
          return <EventStatusBadge status={value} />;
        },
      },
      {
        key: "actions",
        label: "",
        width: 60,
        minWidth: 60,
        align: "left",
        render: (_, record) => (
          <RecordActions
            context={record}
            //onViewClick={onViewEvent}
            onEditClick={onEditEvent}
            onRemoveClick={onRemoveEvent}
            //onGoInClick={
            //  record.recurringEventId ? onViewRecurringEvent : undefined
            //}
            removeInProgress={removingEventId === record.id}
            editDisabled={disabled}
            removeDisabled={disabled || record.isPast || false}
            viewDisabled={disabled || record.isPast || false}
            goInDisabled={disabled || record.isPast || false}
            //disabled={record.isPast}
          />
        ),
      },
    ],
    [
      onViewEvent,
      onEditEvent,
      onRemoveEvent,
      onViewRecurringEvent,
      removingEventId,
    ]
  );

  if (hideOnNoRecords === true && (!events || events.length == 0)) {
    return null;
  }

  return (
    <AppTable
      data={events}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(event) => event?.id || ""}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
